import React from "react";
import {
  Heart,
  Leaf,
  Activity,
  FileText,
  Bell,
  TrendingUp,
} from "lucide-react";

const TenTab = () => {
  const emotionalScore = 75;
  const nutritionalScore = 68;
  const physicalScore = 88;
  const overallScore = 82;

  const CircularProgress = ({
    score,
    size = 120,
    strokeWidth = 8,
    color = "#10b981",
  }) => {
    const radius = (size - strokeWidth) / 2;
    const circumference = radius * 2 * Math.PI;
    const strokeDasharray = circumference;
    const strokeDashoffset = circumference - (score / 100) * circumference;

    return (
      <div className="relative" style={{ width: size, height: size }}>
        <svg width={size} height={size} className="transform -rotate-90">
          <circle
            cx={size / 2}
            cy={size / 2}
            r={radius}
            stroke="#f3f4f6"
            strokeWidth={strokeWidth}
            fill="transparent"
          />
          <circle
            cx={size / 2}
            cy={size / 2}
            r={radius}
            stroke={color}
            strokeWidth={strokeWidth}
            fill="transparent"
            strokeDasharray={strokeDasharray}
            strokeDashoffset={strokeDashoffset}
            strokeLinecap="round"
            className="transition-all duration-1000 ease-out"
          />
        </svg>
        <div className="absolute inset-0 flex items-center justify-center">
          <span className="text-xl md:text-2xl font-bold text-gray-800">
            {score}
          </span>
        </div>
      </div>
    );
  };

  const ScoreCard = ({ title, score, icon: Icon, color, description }) => (
    <div className="flex flex-col items-center text-center p-4 md:p-6 bg-white rounded-2xl shadow-sm border border-gray-100 h-full">
      <div className="mb-4">
        <CircularProgress
          score={score}
          size={100}
          color={color}
          strokeWidth={6}
        />
      </div>
      <div className="flex items-center justify-center mb-2">
        <Icon className="w-5 h-5 mr-2" style={{ color }} />
        <h3 className="font-semibold text-gray-800">{title}</h3>
      </div>
      <p className="text-sm text-gray-600 leading-relaxed">{description}</p>
    </div>
  );

  const FeatureCard = ({ icon: Icon, title, description }) => (
    <div className="flex flex-col items-center text-center p-6 bg-white rounded-xl shadow-sm border border-gray-100 h-full">
      <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center mb-4">
        <Icon className="w-6 h-6 text-green-600" />
      </div>
      <h3 className="font-semibold text-gray-800 mb-2">{title}</h3>
      <p className="text-sm text-gray-600">{description}</p>
    </div>
  );

  return (
    <div className="min-h-screen bg-gradient-to-br from-green-50 via-blue-50 to-purple-50 p-4 md:p-8">
      <div className="max-w-8xl mx-auto">
        {/* Header */}
        <div className="text-center mb-10 md:mb-14">
          <h1 className="text-3xl md:text-5xl font-bold text-gray-800 mb-4">
            Personalized Wellness
            <br className="hidden sm:block" />
            Score System
          </h1>
          <p className="text-base md:text-lg text-gray-600 max-w-2xl mx-auto">
            Three scores, one complete picture of healing —
            <br className="hidden sm:block" />
            updated daily for actionable insights.
          </p>
        </div>

        {/* Score Cards */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 mb-12 items-stretch">
          <ScoreCard
            title="Emotional Score"
            score={emotionalScore}
            icon={Heart}
            color="#ec4899"
            description="Mood patterns, therapy progress, resilience"
          />

          <ScoreCard
            title="Nutritional Score"
            score={nutritionalScore}
            icon={Leaf}
            color="#10b981"
            description="Meal balance, food response tracking"
          />

          <ScoreCard
            title="Physical Score"
            score={physicalScore}
            icon={Activity}
            color="#059669"
            description="Mobility, pain levels, fatigue index"
          />

          {/* Overall Score - centered under the middle card on lg screens */}
          <div className="flex justify-center lg:col-start-2 lg:row-start-2">
            <div className="flex flex-col items-center text-center p-6 bg-white rounded-2xl shadow-lg border border-gray-200 w-full">
              <div className="mb-6">
                <CircularProgress
                  score={overallScore}
                  size={140}
                  color="url(#gradient)"
                  strokeWidth={8}
                />
                <svg width="0" height="0">
                  <defs>
                    <linearGradient
                      id="gradient"
                      x1="0%"
                      y1="0%"
                      x2="100%"
                      y2="100%"
                    >
                      <stop offset="0%" stopColor="#10b981" />
                      <stop offset="50%" stopColor="#059669" />
                      <stop offset="100%" stopColor="#ec4899" />
                    </linearGradient>
                  </defs>
                </svg>
              </div>
              <h3 className="text-lg md:text-xl font-bold text-gray-800 mb-1">
                Overall
              </h3>
              <h2 className="text-xl md:text-2xl font-bold text-gray-800 mb-3">
                Wellness Score
              </h2>
              <p className="text-sm text-gray-600 leading-relaxed">
                Your daily overall score, calculated in real-time
              </p>
            </div>
          </div>
        </div>

        {/* Features Section */}
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-6 mb-12">
          <FeatureCard
            icon={FileText}
            title="Assign customized recovery modules"
            description="Personalized wellness programs tailored to your specific needs and goals"
          />
          <FeatureCard
            icon={Bell}
            title="Trigger timely nudges"
            description="Smart notifications and reminders to keep you on track with your wellness journey"
          />
          <FeatureCard
            icon={TrendingUp}
            title="Track healing progress"
            description="Monitor your improvement over time with detailed analytics and milestone tracking"
          />
        </div>

        {/* Bottom Message */}
        <div className="text-center">
          <p
            className="text-base md:text-lg text-gray-700 mb-8 max-w-2xl mx-auto"
            style={{
              backgroundColor: "#ffeef1",
              color: "#1a1a1a",
              padding: "1rem 1.5rem",
              borderRadius: "20px",
              fontWeight: 500,
            }}
          >
            Numbers that care—not just track. Every score is designed to guide
            recovery.
          </p>

          <button className="bg-green-600 hover:bg-green-700 text-white font-semibold py-3 md:py-4 px-6 md:px-8 rounded-full text-base md:text-lg transition-all duration-300 shadow-lg hover:shadow-xl transform hover:scale-105">
            Check My Wellness Score
          </button>
        </div>
      </div>
    </div>
  );
};

export default TenTab;
